/**
 * reCAPTCHA v3 — provider + hook helpers.
 *
 * Uso:
 *   1. Envolver a app com `<RecaptchaProvider>` (já feito em `index.js`).
 *   2. Em formulários sensíveis: `const exec = useRecaptchaExecute();`
 *      antes de submeter: `const token = await exec("checkout"); body.recaptcha_token = token;`
 *
 * Se a env `REACT_APP_RECAPTCHA_SITE_KEY` não estiver definida, o hook
 * devolve `null` (modo dev — backend faz bypass com warning).
 */

import { useCallback } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export function RecaptchaProvider({ children }) {
  if (!SITE_KEY) {
    // Dev fallback — sem provider (warnings já no backend).
    return children;
  }
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_KEY}
      scriptProps={{ async: true, defer: true, appendTo: "head" }}
      // Esconder o badge — o utilizador é informado nos termos do form.
      container={{ parameters: { badge: "bottomright" } }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
}

/**
 * Devolve uma função `exec(action)` que gera um token v3 para a action dada.
 * Se reCAPTCHA não estiver carregado (ex: site key ausente), devolve string vazia.
 */
export function useRecaptchaExecute() {
  const { executeRecaptcha } = useGoogleReCaptcha() || {};
  return useCallback(
    async (action = "submit") => {
      if (!SITE_KEY || !executeRecaptcha) return "";
      try {
        return await executeRecaptcha(action);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[recaptcha] execute failed:", e);
        return "";
      }
    },
    [executeRecaptcha],
  );
}
